/*======================================================================================================================
  * Bluebell: src/app/@state/part-libraries/part-library.effects.ts
  * Last Edited: 07/01/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { EffectHandlerService } from '@services/effect-handler.service';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { ApiService } from '@state/api.service';
import {
  selectInactiveLibrariesLoaded,
  selectLoadInactivePartLibraries,
} from '@state/part-libraries/part-library.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { partLibraryActions } from '@state/part-libraries/part-library.actions';
import {
  Part,
  PartLibrary,
  PartLibraryUpdatePartial, PartTransform, PartUpdatePartial,
} from '@state/part-libraries/part-library.interfaces';
import { concatLatestFrom } from '@ngrx/operators';
import { selectCurrentProgramme } from '@state/programmes/programme.selectors';
import { mergeMap, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';


@Injectable()
export class PartLibraryEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private handler = inject(EffectHandlerService);
  private messageService = inject(MessageService);


  loadInactive = this.store.selectSignal(selectLoadInactivePartLibraries);

  loadLibraries$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.load),
    concatLatestFrom(() => this.store.select(selectCurrentProgramme)),
    switchMap(([, programme]) => this.api.listPartLibraries(!this.loadInactive(), programme?.uuid).pipe(
      this.handler.effectResponse({
        success: (partLibraries: PartLibrary[]) => partLibraryActions.loadSuccess(partLibraries.reverse()),
        retry: partLibraryActions.load(),
        failure: partLibraryActions.loadFailure(),
        failureMessage: 'Error loading parts, please try again or contact support@bramblecfd.com.'
      }),
    ))
  ));

  toggleLoadInactive$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.toggleLoadInactive),
    concatLatestFrom(() => [
      this.store.select(selectLoadInactivePartLibraries),
      this.store.select(selectInactiveLibrariesLoaded),
    ]),
    map(({1:loadInactive, 2:inactiveLibrariesLoaded}) => {
      // When retrieved from the store loadInactive is the value AFTER it has been toggled
      // As the reducer takes effect at the same time this effect is called.
      // only call another load if the inactive libraries are not already loaded
      if (loadInactive && !inactiveLibrariesLoaded) {
        this.store.dispatch(partLibraryActions.load());
      }
    })
  ), { dispatch: false });

  createPartLibrary$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.create),
    mergeMap((data: { programme: string, name: string }) => this.api.createPartLibrary(data).pipe(
      this.handler.effectResponse({
        success: (newPartLibrary: PartLibrary) => partLibraryActions.createSuccess(newPartLibrary),
        retry: partLibraryActions.create(data),
        failure: partLibraryActions.createFailure(),
        successMessage: `Successfully Created Part Library: ${data.name}`,
        failureMessage: `Error Creating Part Library: ${data.name}`,
        showErrorDetail: true
      }),
    ))
  ));

  deletePartLibrary$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.delete),
    mergeMap((partLibrary: PartLibrary) => this.api.deletePartLibrary(partLibrary.uuid).pipe(
      this.handler.effectResponse({
        success: () => partLibraryActions.deleteSuccess(partLibrary.uuid),
        retry: partLibraryActions.delete(partLibrary),
        failure: partLibraryActions.deleteFailure(),
        failureMessage: `Error deleting Part Library ${partLibrary.name}`,
        showErrorDetail: true,
      })
    )),
  ));

  updatePartLibrary$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.update),
    mergeMap((partLibraryPartial: PartLibraryUpdatePartial) => this.api.updatePartLibrary(partLibraryPartial).pipe(
      this.handler.effectResponse({
        success: (partLibrary: PartLibrary) => partLibraryActions.updateSuccess(partLibrary),
        retry: partLibraryActions.update(partLibraryPartial),
        failure: partLibraryActions.updateFailure(),
        successMessage: `Successfully updated Part Library ${partLibraryPartial.name}`,
        failureMessage: `Error updating Part Library ${partLibraryPartial.name}`,
        showErrorDetail: true,
      })
    ))
  ));

  reloadPartLibrary$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.reload),
    mergeMap(({ uuid }) => this.api.retrievePartLibrary(uuid).pipe(
      this.handler.effectResponse({
        success: (partLibrary: PartLibrary) => partLibraryActions.reloadSuccess(partLibrary),
        retry: partLibraryActions.reload(uuid),
        failure: partLibraryActions.reloadFailure(),
        failureMessage: 'Something went wrong when updating a part library, please contact support@bramblecfd.com',
        showErrorDetail: true
      })
    ))
  ));

  /* Part Effects */

  deletePart$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.deletePart),
    mergeMap(({ part, library }: {part: Part, library: string}) => {
      this.messageService.add({
        severity: 'info',
        summary: 'Part Deletion',
        detail: `Processing part deletion for "${part.name}".`,
        life: 4000,
      });
      return this.api.deletePart(part.uuid).pipe(
        this.handler.effectResponse({
          success: () => partLibraryActions.deletePartSuccess({ part, library }),
          successMessage: `Successfully deleted Part ${part.name}`,
          retry: partLibraryActions.deletePart({ part, library }),
          failure: partLibraryActions.deletePartFailure(),
          failureMessage: 'Something went wrong, please contact support@bramblecfd.com',
          showErrorDetail: true,
        }),
      );
    })
  ));

  updatePart$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.updatePart),
    mergeMap((partial: PartUpdatePartial) => this.api.updatePart(partial).pipe(
      this.handler.effectResponse({
        success: (part: Part) => partLibraryActions.updatePartSuccess(part),
        successMessage: `Successfully updated Part ${partial.name}`,
        retry: partLibraryActions.updatePart(partial),
        failure: partLibraryActions.updatePartFailure(),
        failureMessage: `Something went wrong updating Part Library ${partial.name}, Please contact support@bramblecfd.com`,
        showErrorDetail: true,
      })
    ))
  ));

  transformPart$ = createEffect(() => this.actions$.pipe(
    ofType(partLibraryActions.transformPart),
    mergeMap((transform: PartTransform) => {
      this.messageService.add({
        severity: 'info',
        summary: 'Part Transformation',
        detail: 'Part transformation is being processed.',
        life: 4000,
      });
      return this.api.transformPart(transform).pipe(
        this.handler.effectResponse({
          success: (part: Part) => partLibraryActions.transformPartSuccess(part),
          retry: partLibraryActions.transformPart(transform),
          failure: partLibraryActions.transformPartFailure(),
          successMessage: 'Part Transformation Complete, new part successfully created',
          failureMessage: 'An error occurred while transforming a part, please contact support@bramblecfd.com',
          showErrorDetail: true,
        }),
      );
    })
  ));

  // Clear the store completely when the account is changed
  clearStore$ = createEffect(() => this.actions$.pipe(
    ofType(programmeProjectSelectionUnapplied),
    map(() => partLibraryActions.clearAll()),
  ));
}
